(ns app.front.view.planning-report.core
  (:require [app.domain.application.condition.model :as application-condition-model]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.planning-report.model :as planning-report-model]
            [app.domain.property.model :as property-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as table]
            [app.front.ui.typography :refer [h1 h2 h3 p]]
            [app.front.util :as futil]
            [app.front.view.attachment.select-list :as attachment-select-list]
            [app.front.view.not-found :as not-found]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r])
  (:require-macros [core.util.util :as util]))


;; Force rerender of the approval comments rich text editor when "Refresh comments" is clicked
(defonce *force-rerender-approval-comments (r/atom 0))

(rf/reg-event-fx ::fetch-single-report-issues
  (fn [{:keys [db]} [_ workspace-id report-id]]
    {:db           (assoc-in db [::planning-reports :loading?] true)
     :comm/command {:command    :planning-report/get-issues
                    :method     :get
                    :data       {:workspace-id workspace-id
                                 :report-id    report-id}
                    :on-success [::fetch-single-report-issues-success report-id]}}))


(rf/reg-event-db ::fetch-single-report-issues-success
  (fn [db [_ report-id _ issues]]
    (-> db
        (assoc-in [::planning-reports :report-issues report-id] issues)
        (p/dissoc-in [::planning-reports :loading?]))))


(rf/reg-event-fx ::fetch-templates
  (fn [_ [_ report-id]]
    {:comm/command {:command    :planning-report/available-templates
                    :method     :get
                    :data       {:report-id report-id}
                    :on-success [::fetch-templates.success report-id]}}))


(rf/reg-event-db ::fetch-templates.success
  (fn [db [_ report-id _ templates]]
    (assoc-in db [::planning-reports :template-options report-id] templates)))


(rf/reg-event-fx ::refresh-approval-comments
  (fn [{:keys [db]} [_ report-id application-id]]
    {:db       (-> db
                   (assoc-in [::planning-reports :update-comments-loading?] true)
                   (p/dissoc-in [::planning-reports :report-issues report-id :new-comments?]))
     :dispatch [:app.front.process/signal
                application-id
                [:process.signal/refresh-approval-comments {:id report-id}]
                {:on-success [::comments-loaded]
                 :notify     {:success :planning-reports/comments-refreshed}}]}))


(rf/reg-event-fx ::append-all-approval-comments
  (fn [{:keys [db]} [_ report-id application-id]]
    {:db       (assoc-in db [::planning-reports :update-comments-loading?] true)
     :dispatch [:app.front.process/signal
                application-id
                [:process.signal/append-all-approval-comments {:id report-id}]
                {:on-success [::comments-loaded]
                 :notify     {:success :planning-reports/comments-refreshed}}]}))


(rf/reg-event-fx ::comments-loaded
  (fn [{:keys [db]} _]
    (swap! *force-rerender-approval-comments inc)
    {:db       (p/dissoc-in db [::planning-reports :update-comments-loading?])
     :dispatch [::scroll-to-bottom]}))


(rf/reg-event-fx ::scroll-to-bottom
  (fn [_ _]
    (futil/scroll-to-bottom)
    {}))


(defn- create-delete-signal [signal db workspace-id application-id report-id]
  {:db       (assoc-in db [::planning-reports :loading?] true)
   :dispatch [:app.front.process/signal
              application-id
              [signal {:id report-id}]
              {:on-success [::delete-success report-id workspace-id]
               :on-error   [::clear-loading report-id]
               :notify     {:success :planning-reports/deleted}}]})


(rf/reg-event-fx ::delete-draft-report
  (fn [{:keys [db]} [_ workspace-id application-id draft-report-id]]
    (create-delete-signal :process.signal/delete-draft-report db workspace-id application-id draft-report-id)))


(rf/reg-event-fx ::delete-final-report
  (fn [{:keys [db]} [_ workspace-id application-id final-report-id]]
    (create-delete-signal :process.signal/delete-final-report db workspace-id application-id final-report-id)))


(rf/reg-event-fx ::delete-success
  (fn [_ [_ report-id workspace-id]]
    ;; Navigate back to application
    {:dispatch-n [[:route/push {:route  :view/workspace-application
                                :params {:workspace-id workspace-id}}]
                  [::clear-loading report-id]]}))

(rf/reg-event-db ::clear-loading
  (fn [db _]
    (p/dissoc-in db [::planning-reports :loading?])))

(rf/reg-sub ::planning-reports-state
  :-> ::planning-reports)

(rf/reg-sub ::report-issues
  :<- [::planning-reports-state]
  (fn [planning-reports [_ id]]
    (get-in planning-reports [:report-issues id])))


(rf/reg-sub ::report-topics
  (fn [[_ id]]
    (rf/subscribe [::report-issues id]))
  :-> :topics)


(rf/reg-sub ::template-options
  :<- [::planning-reports-state]
  (fn [planning-reports [_ id]]
    (get-in planning-reports [:template-options id])))


(rf/reg-sub ::party
  (fn [[_ app-id] _]
    [(rf/subscribe [::entities/entities {:entity/type   :entity.type/party
                                         :entity/parent app-id}])])
  (fn [[parties] [_ _ role-key]]
    (planning-report-model/get-party-name parties role-key)))


(rf/reg-sub ::total-site-area
  (fn [[_ app-id] _]
    [(rf/subscribe [::entities/entity {:db/id app-id}])])
  (fn [[app] _]
    (planning-report-model/get-total-site-area app)))


(rf/reg-sub ::loading?
  :<- [::planning-reports-state]
  :-> :loading?)


(rf/reg-sub ::update-comments-loading?
  :<- [::planning-reports-state]
  (fn [planning-reports _]
    (-> planning-reports :update-comments-loading? boolean)))


(defn save-handlers [signal app-id report-id published?]
  (fn [attr val-fn]
    (when-not published?
      {:on-change #((ee/save-debounce-fn signal) app-id report-id attr (val-fn %))
       :on-blur   #((ee/save-fn signal) app-id report-id attr (val-fn %))})))


(def max-width {:max-width "1000px"})


(defn general-information [{:planning-report/keys [rezoning-file previous-spc-file by-law]}
                           {:keys [tr app-id properties published? save-fns]}]
  (let [owner           (<sub [::party app-id :role/property-owner])
        agent           (<sub [::party app-id :role/agent])
        total-site-area (<sub [::total-site-area app-id])
        field           :div.grid.grid-cols-1.lg:grid-cols-2.items-center.mb-4
        label           :label.block.mb-2.lg:mb-0]
    [:<>
     [h2 "mb-4" (tr [:planning-reports/general-information])]
     [:form.grid.grid-cols-1.lg:grid-cols-2
      [:div.mb-4.lg:mb-8.col-span-1
       [h3 "mb-2" (tr [:planning-reports/owner])]
       [p "mb-4 lg:mb-8 uppercase" owner]
       [h3 "mb-2" (tr [:planning-reports/agent])]
       [p "mb-4 lg:mb-8 uppercase" agent]]
      [:div.lg:px-4.mb-8.col-span-1
       [:div.grid.grid-cols-1.lg:grid-cols-2.mb-4 {:class (when-not published? "lg:mb-8")}
        [h3 "mb-2 lg:mb-0" (tr [:planning-reports/total-site-area])]
        [p total-site-area]]
       [:div.grid.grid-cols-1.lg:grid-cols-2.mb-4
        [h3 "mb-2 lg:mb-0" (tr [:planning-reports/zoning])]
        [p (planning-report-model/concat-zoning properties)]]
       [field {:class (when-not published? "lg:mb-0")}
        [label {:for "rezoning-file"} (tr [:planning-reports/rezoning-file])]
        (if-not published?
          [ui/text-input (merge (save-fns :planning-report/rezoning-file futil/tval)
                                {:id            "rezoning-file"
                                 :default-value rezoning-file})]
          [p rezoning-file])]
       [field {:class (when-not published? "lg:mb-0")}
        [label {:for "previous-spc-file"} (tr [:planning-reports/previous-spc-file])]
        (if-not published?
          [ui/text-input (merge (save-fns :planning-report/previous-spc-file futil/tval)
                                {:id            "previous-spc-file"
                                 :default-value previous-spc-file})]
          [p previous-spc-file])]
       [field {:class (when-not published? "lg:mb-0")}
        [label {:for "by-law"} (tr [:planning-reports/by-law])]
        (if-not published?
          [ui/text-input (merge (save-fns :planning-report/by-law futil/tval)
                                {:id            "by-law"
                                 :default-value by-law})]
          [p by-law])]]]]))


(defn address [{:keys [tr workspace properties]}]
  [:<>
   [h2 "lg:mb-4" (tr [:planning-reports/description-of-subject-lands])]
   [table/modern-responsive-table
    {:items (->> properties
                 (sort-by (workspace-model/property-sort-fn workspace)))
     :tr    tr
     :cols  [[:planning-reports/municipal-address
              property-model/full-address
              {:width "30%"}]
             [(tr [:property/id])
              :property/id
              {:width "190px"
               :flex  "0"}]
             [(tr [:property/legal])
              :property/legal]]}]])


(defn template-selection [{:keys [tr report edit-signal app-id published?]}]
  (let [template-options (<sub [::template-options (:db/id report)])]
    (when (> (count template-options) 1)
      [:div.mt-3.mb-3
       [h2 "mb-4" (tr [:planning-reports/template-selection-heading])]
       [:div.w-full.lg:w-1-2.xl:w-1-3
        [ui-select/select {:id           "planning-report-template-selection"
                           :class-name   "full-width-select"
                           :label-text   (tr [:planning-reports/template-selection-heading])
                           :hide-label   true
                           :value        (-> report :planning-report/template :db/id)
                           :values       (map :template-id template-options)
                           :allow-empty? false
                           :val->str     #(-> template-options
                                              (p/find-first {:template-id %})
                                              :group/name)
                           :disabled     (boolean published?)
                           :on-change    #((ee/save-fn edit-signal) app-id (:db/id report) :planning-report/template %)}]]])))


(defn authority-group-id-for-report [report]
  (-> report
      :entity/parent
      :entity/parent
      :domain/authority-group
      :db/id))


(defn report-editor [report field-key {:keys [tr save-fns published? title-tr-key attr id class]}]
  [:div {:style max-width
         :class (or class "py-4")}
   (when title-tr-key
     [h2 (if published? "mb-2" "mb-4") (tr [title-tr-key])])
   [ui-rt/editor-legacy (merge (save-fns attr identity)
                               {:initial-value      (field-key report)
                                :id                 id
                                :authority-group-id (authority-group-id-for-report report)})]])


(defn draft-recommendation-editor [report opts]
  [report-editor report :planning-report/draft-recommendation (merge opts {:title-tr-key :planning-reports/draft-recommendation
                                                                           :attr         :planning-report/draft-recommendation
                                                                           :id           "draft-report-recommendation"})])


(defn recommendation-editor [report opts]
  [report-editor report :planning-report/recommendation (merge opts {:title-tr-key :planning-reports/recommendation
                                                                     :attr         :planning-report/recommendation
                                                                     :id           "report-recommendation"})])


(defn revisions-and-clarifications-editor [report opts]
  [report-editor report :planning-report/revisions-and-clarifications (merge opts {:attr  :planning-report/revisions-and-clarifications
                                                                                   :id    "revisions-and-clarifications"
                                                                                   :class "pb-4"})])


(defn decision-editor [report opts]
  [report-editor report :planning-report/decision (merge opts {:title-tr-key :planning-reports/decision
                                                               :attr         :planning-report/decision
                                                               :id           "decision"})])

(rf/reg-sub ::attachment-type-name
  (fn [[_ attachment-type-id]]
    [(rf/subscribe [:language])
     (rf/subscribe [::entities/entity attachment-type-id])])
  (fn [[lang at] _]
    (attachment-model/attachment-type-label at lang)))

(defn revisions-and-clarifications [{:db/keys [id] :as report} {:keys [tr loading?] :as opts}]
  (let [{:keys [form-issues
                attachment-issues]} (<sub [::report-issues id])
        form-issues?                (seq form-issues)
        attachment-issues?          (seq attachment-issues)
        table-opts                  {:tr                    tr
                                     :expandable?           false
                                     :class                 "mb-4"
                                     :header-class-override "p-2 bg-ui-gray font-bold"
                                     :row-class-override    "lg:items-center  p-2 odd:bg-ui-light-gray"}]
    [:div.py-4 {:style max-width}
     [h2 "mb-4" (tr [:planning-reports/revisions-and-clarifications])]
     [revisions-and-clarifications-editor report opts]
     (if loading?
       (cc/skeleton-text)
       [:<>
        (when form-issues?
          [:<>
           [h3 "lg:hidden mb-2 font-bold" (tr [:planning-reports/forms])]
           #_{:clj-kondo/ignore [:deprecated-var]}
           [table/responsive-table table-opts
            [{:title (tr [:planning-reports/form-name])}
             {:title (tr [:planning-reports/requested-change])}]
            (into
              []
              (map (fn [form-issue]
                     {:key  (-> form-issue :form-name)
                      :cols [{:opts    {:class "leading-tight"}
                              :content (-> form-issue :form-name)}
                             {:opts    {:class "leading-tight"}
                              :content (-> form-issue :issue)}]}))
              form-issues)]])

        (when attachment-issues?
          [:<>
           [h3 "lg:hidden mb-2 font-bold" (tr [:planning-reports/attachments])]
           #_{:clj-kondo/ignore [:deprecated-var]}
           [table/responsive-table table-opts
            [{:title (tr [:planning-reports/attachment-type])}
             {:title (tr [:planning-reports/requested-change])}]
            (into
              []
              (map (fn [attachment-issue]
                     (let [types (->> (:attachment-types attachment-issue)
                                      (map #(<sub [::attachment-type-name %]))
                                      (str/join ", "))]
                       {:key  types
                        :cols [{:opts    {:class "leading-tight"}
                                :content types}
                               {:opts    {:class "leading-tight"}
                                :content (-> attachment-issue :issue)}]})))
              attachment-issues)]])])]))


(def condition-state->color {:condition/draft  "bg-status-warning text-ui-black"
                             :condition/open   "bg-status-error text-ui-white"
                             :condition/closed "bg-status-success text-ui-white"})

(defn conditions [{:db/keys [id]} {:keys [tr loading?]}]
  (let [{:keys [open-conditions closed-conditions]} (<sub [::report-issues id])
        conditions                                  (->> open-conditions
                                                         (concat closed-conditions)
                                                         (sort-by :order))]
    [:<>
     [h2 "mb-4" (tr [:planning-reports/pre-approval-conditions])]
     (if loading?
       (cc/skeleton-text)
       (if (empty? conditions)
         [p "mb-4" (tr [:planning-reports/no-open-conditions])]
         [:ul.mb-4 {:style max-width}
          (futil/for-frag [condition conditions
                           :let [{:keys [title body]} condition
                                 draft?   (application-condition-model/draft? condition)
                                 cleared? (some? (:cleared condition))
                                 state    (cond draft? :condition/draft
                                                cleared? :condition/closed
                                                :else :condition/open)
                                 colors   (condition-state->color state)]]
            [:li.flex.bg-ui-light-gray.mb-2
             [:div.w-24.flex.flex-col.p-4.items-center.text-center.uppercase
              {:class colors}
              ^{:key (str "condition" title)}
              [:div.mb-2.font-bold
               (:order condition)]
              [h3 "font-bold" (tr [state])]]
             [:div.w-full.flex.flex-col.p-4
              [:div.flex.flex-1.flex-col.md:flex-row
               [:div.flex-1.pb-0
                [h2 "font-bold mb-2" title]]]
              [:div.flex.flex-1.flex-col.md:flex-row
               [:p.p-0.leading-tight {:style {:max-width "900px"}} body]]
              (when cleared?
                [:div.pt-4.text-xs (tr [:condition/cleared]) " "
                 [:span.font-bold ((<sub [::date-util/date-fn]) (:cleared condition))]
                 " " (tr [:condition/by]) " "
                 [:span.font-bold (-> condition :closed-by)]])]])]))]))


(def comment-state->color {:checklist.comment.state/draft "bg-status-warning text-ui-black"
                           :checklist.comment.state/open  "bg-status-error text-ui-white"})


(defn comments [{:keys [comments]} tr]
  [:ul.mb-4 {:style max-width}
   (futil/for-frag [[idx comment] (map-indexed vector comments)
                    :let [{:keys [title body state]} comment
                          colors (comment-state->color state)]]
     [:li.flex.bg-ui-light-gray.mb-2 {:key (str "comment" idx)}
      [:div.w-24.flex.flex-col.p-4.items-center.text-center.uppercase
       {:class colors}
       [:div.mb-2.font-bold (inc idx)]
       [h3 "font-bold" (tr [state])]]
      [:div.w-full.flex.flex-col.p-4
       [:div.flex.flex-1.flex-col.md:flex-row
        [:div.flex-1.pb-0
         [h2 "font-bold mb-2" title]]]
       [:div.flex.flex-1.flex-col.md:flex-row
        [:p.p-0.leading-tight {:style {:max-width "900px"}} body]]]])])


(defn topics [report-id {:keys [tr loading?]}]
  (let [topics (<sub [::report-topics report-id])]
    (when (seq topics)
      [:<>
       [h2 "mb-4" (tr [:planning-reports/comments])]
       (if loading?
         (cc/skeleton-text)
         (futil/for-frag [topic topics
                          :let [{:keys [order title]} topic]]
           [:div {:key (str "topic" order)}
            [h3 "mb-2 font-bold" (str order ". " title)]
            [comments topic tr]]))])))


(defn approval-comments [{:db/keys              [id]
                          :planning-report/keys [approval-comments]}
                         {:keys [tr save-fns published? app-id]}]
  (r/with-let [*modal-open? (r/atom false)
               on-close     #(reset! *modal-open? false)]
    (let [loading?              (<sub [::update-comments-loading?])
          can-append-comments?  (<sub [:app.front.process/app-signal-permitted? [:process.signal/append-all-approval-comments]])
          can-refresh-comments? (<sub [:app.front.process/app-signal-permitted? [:process.signal/refresh-approval-comments {:id id}]])
          show-add-comments?    (and can-append-comments? (not published?))]
      [:<>
       [ui-modal/composed-input-modal
        {:open                   @*modal-open?
         :selector-primary-focus "button.bx--modal-close"
         :on-close               (fn [_]
                                   (on-close)
                                   true)}
        (cc/modal-header
          {:close-modal on-close
           :title       (tr [:planning-reports/add-comments])})
        (cc/modal-body
          {}
          (if can-refresh-comments?
            (tr [:planning-reports/add-comments-body])
            (tr [:planning-reports/no-new-comments-body])))
        (cc/modal-footer
          {}
          (cc/button
            {:kind     :secondary
             :on-click on-close}
            (tr [:cancel]))
          (cc/button
            {:kind     (if can-refresh-comments? :tertiary :primary)
             :on-click (fn []
                         (on-close)
                         (>evt [::append-all-approval-comments id app-id]))}
            (tr [:planning-reports/add-all]))
          (cc/button
            {:kind     :primary
             :disabled (not can-refresh-comments?)
             :on-click (fn []
                         (on-close)
                         (>evt [::refresh-approval-comments id app-id]))}
            (tr [:planning-reports/add-new])))]
       [:div.py-4 {:style max-width}
        [:div.flex.justify-between.items-center {:class (when-not published? "mb-4")}
         [h2 "mb-2" (tr [:planning-reports/approval-comments])]
         (when show-add-comments?
           (cc/button
             {:size     :small
              :kind     :tertiary
              :disabled loading?
              :on-click #(reset! *modal-open? true)}
             (tr [:planning-reports/add-comments])))]

        ^{:key (str "approval-comments-" @*force-rerender-approval-comments)}
        [ui-rt/editor-legacy (merge (save-fns :planning-report/approval-comments identity)
                                    {:initial-value approval-comments
                                     :id            "draft-report-approval-comments"})]]])))


(defn delete-button [{:keys [tr published? workspace-id app-id id is-draft-report?]}]
  (when-not published?
    [ui/modal-confirmation-button
     {:tr                  tr
      :id                  "delete-draft-report"
      :trigger-button-text (tr [:planning-reports/delete-report])
      :trigger-button-opts {:class-name "mt-4"
                            :kind       :danger
                            :icon       i/remove}
      :on-yes-click        #(>evt [(if is-draft-report?
                                     ::delete-draft-report
                                     ::delete-final-report) workspace-id app-id id])}
     (tr [:planning-reports/confirm-delete-report])]))

(defn attachments-view [{:keys [report application]}]
  (when report
    (r/with-let [*idx-vector (r/atom (or (->> report
                                              :entity/_parent
                                              (filter (eu/is-type-fn :entity.type/planning-report-attachment))
                                              (sort-by :ordered/order)
                                              (map :planning-report-attachment/attachment)
                                              (map :db/id))
                                         []))]
      (let [attachments (->> application
                             :entity/_parent
                             (filter (eu/is-type-fn :entity.type/attachment)))]
        [attachment-select-list/select-list
         (util/mmap {:editing?   (<sub [::process/app-signal-permitted? [:process.signal/set-report-attachments]])
                     :on-change  (fn [new-idx-vector]
                                   (reset! *idx-vector new-idx-vector)
                                   (>evt [::process/signal application [:process.signal/set-report-attachments
                                                                        {:attachment-ids new-idx-vector
                                                                         :report-id      (:db/id report)}]]))
                     :idx-vector @*idx-vector}
                    attachments)]))))

(defn render-report [report workspace-id application tr]
  (let [is-draft-report? (eu/is-type? :entity.type/planning-draft-report report)
        id               (:db/id report)
        app-id           (:db/id application)
        workspace        (:entity/parent application)
        tz               (-> workspace :domain/authority-group :group/tz)
        properties       (:workspace/properties workspace)
        published-at     (:planning-report/published report)
        published?       (some? published-at)
        publish-date     (date-util/in-tz-with-opts published-at tz {:month "numeric" :day "numeric" :year "numeric"})
        edit-signal      (if is-draft-report? :process.signal/edit-draft-report :process.signal/edit-final-report)
        save-fns         (save-handlers edit-signal app-id id published?)
        loading?         (<sub [::loading?])
        title            (if is-draft-report? :planning-reports/draft-report-title :planning-reports/final-report-title)]
    [:article.bg-ui-white
     [:header.mb-4.flex.justify-between.items-center.px-8.py-4
      {:class (if published? "bg-ui-dark-gray" "bg-ui-yellow")}
      [h1 (tr [title])]
      (if published?
        [p (str (tr [:planning-reports/published]) " " publish-date)]
        [p (tr [:planning-reports/all-changes-saved-automatically])])]

     [:div.px-8.pb-8
      [general-information report (util/mmap tr app-id published? save-fns properties)]
      [address (util/mmap tr workspace properties)]
      [template-selection (util/mmap tr report edit-signal app-id published?)]
      (if is-draft-report?
        [draft-recommendation-editor report (util/mmap tr save-fns published?)]
        [recommendation-editor report (util/mmap tr save-fns published?)])
      (when is-draft-report?
        [revisions-and-clarifications report (util/mmap tr save-fns published? loading?)])
      [conditions report (util/mmap tr loading?)]
      [topics id (util/mmap tr loading?)]
      (when-not is-draft-report?
        [decision-editor report (util/mmap tr save-fns published?)])
      [approval-comments report (util/mmap tr save-fns published? app-id)]
      (when-not is-draft-report?
        [attachments-view (util/mmap report application)])
      [delete-button (util/mmap tr published? workspace-id app-id id is-draft-report?)]]]))


(defn report-view [match]
  (let [{:keys [workspace-id report-id]} (-> match :parameters :path)
        workspace                        (<sub [::entities/entity {:workspace/id workspace-id}])
        application                      (workspace-model/workspace-application workspace)
        tr                               (<sub [::ws-subs/tr])
        not-found?                       (<sub [::ws-subs/not-found])
        report                           (->> application
                                              :entity/_parent
                                              (filter #(= report-id (:db/id %)))
                                              first)]
    (if not-found?
      [not-found/not-found-view]
      [:div.cloudpermit-page
       [render-report report workspace-id application tr]])))


;; Shared route handler between final and draft reports, used in routing
(defn on-navigate-to
  [params]
  (futil/scroll-to-top)
  (let [workspace-id (-> params :path :workspace-id)
        report-id    (-> params :path :report-id)]
    (>evt [::fetch-single-report-issues workspace-id report-id])
    (>evt [::fetch-templates report-id])))
